import React from "react";
import { Card, Row, Col } from "react-bootstrap";
import {
  FileEarmarkText,
  CashStack,
  Send,
  CheckCircleFill,
  XCircleFill,
  ClockFill,
  CloudUploadFill,
  ExclamationTriangleFill,
  ArrowUpRight,
  ArrowDownRight,
} from "react-bootstrap-icons";
import ApexCharts from "react-apexcharts";

const CARDS_DATA = [
  {
    title: "TOTAL CLAIMS",
    value: "18,542",
    trend: "+8.4% vs prev",
    trendUp: true,
    note: "vs. previous period",
    icon: FileEarmarkText,
    iconBg: "#eef5ff",
    iconColor: "#2c7ce6",
    iconBorder: "#cde4ff",
    sparklineColor: "#2c7ce6",
    sparklineData: [140, 150, 145, 160, 158, 170, 185],
    hoverBorder: "#2c7ce6",
  },
  {
    title: "TOTAL GROSS AMOUNT",
    value: "SAR 4,850,200",
    trend: "+9% vs prev",
    trendUp: true,
    note: "Gross provider tariff va...",
    icon: CashStack,
    iconBg: "#eef5ff",
    iconColor: "#2c7ce6",
    iconBorder: "#cde4ff",
    sparklineColor: "#2c7ce6",
    sparklineData: [380, 400, 410, 430, 450, 460, 485],
    hoverBorder: "#2c7ce6",
  },
  {
    title: "TOTAL NET SUBMITTED",
    value: "SAR 4,320,100",
    trend: "+8.5% vs prev",
    trendUp: true,
    note: "Net after patient copay",
    icon: Send,
    iconBg: "#eef5ff",
    iconColor: "#2c7ce6",
    iconBorder: "#cde4ff",
    sparklineColor: "#2c7ce6",
    sparklineData: [350, 370, 365, 390, 410, 420, 432],
    hoverBorder: "#2c7ce6",
  },
  {
    title: "TOTAL APPROVED AMOUNT",
    value: "SAR 3,850,200",
    valueColor: "#00965e",
    badgeText: "89.1% Approval",
    badgeBg: "#e7faf1",
    badgeColor: "#00965e",
    trend: "+10.6% vs prev",
    trendUp: true,
    note: "89.1% Approval Rate",
    icon: CheckCircleFill,
    iconBg: "#e7faf1",
    iconColor: "#00965e",
    iconBorder: "#bcf0da",
    sparklineColor: "#00965e",
    sparklineData: [300, 310, 330, 340, 360, 370, 385],
    leftBorder: "3px solid #00965e",
    hoverBorder: "#00965e",
  },
  {
    title: "TOTAL REJECTED AMOUNT",
    value: "SAR 470,000",
    valueColor: "#dc3545",
    badgeText: "10.9% Rejection",
    badgeBg: "#fde8e8",
    badgeColor: "#dc3545",
    trend: "-6% vs prev",
    trendUp: false,
    note: "10.9% Rejection Rate",
    icon: XCircleFill,
    iconBg: "#fde8e8",
    iconColor: "#dc3545",
    iconBorder: "#f8b4b4",
    sparklineColor: "#dc3545",
    sparklineData: [52, 50, 49, 51, 48, 47, 47],
    leftBorder: "3px solid #dc3545",
    hoverBorder: "#dc3545",
  },
  {
    title: "PENDING AMOUNT",
    value: "SAR 260,000",
    trend: "-8.7% vs prev",
    trendUp: false,
    note: "1,240 claims in adjudic...",
    icon: ClockFill,
    iconBg: "#eef5ff",
    iconColor: "#2c7ce6",
    iconBorder: "#cde4ff",
    sparklineColor: "#f5803e",
    sparklineData: [29, 28, 30, 27, 28, 27, 26],
    hoverBorder: "#2c7ce6",
  },
  {
    title: "READY TO SUBMIT",
    value: "1,132 Claims",
    trend: "-9.4% vs prev",
    trendUp: false,
    note: "SAR 530,100 waiting b...",
    icon: CloudUploadFill,
    iconBg: "#eef5ff",
    iconColor: "#2c7ce6",
    iconBorder: "#cde4ff",
    sparklineColor: "#2c7ce6",
    sparklineData: [125, 120, 122, 118, 115, 114, 113],
    hoverBorder: "#2c7ce6",
  },
  {
    title: "VALIDATION ERRORS",
    titleColor: "#dc3545",
    value: "284 Claims",
    valueColor: "#dc3545",
    badgeText: "• Action",
    badgeBg: "#dc3545",
    badgeColor: "#ffffff",
    trend: "-18.8% vs prev",
    trendUp: false,
    note: "SAR 145,200 needs cor...",
    icon: ExclamationTriangleFill,
    iconBg: "#fde8e8",
    iconColor: "#dc3545",
    iconBorder: "#f8b4b4",
    sparklineColor: "#dc3545",
    sparklineData: [35, 34, 33, 31, 30, 29, 28],
    leftBorder: "3px solid #dc3545",
    cardBorder: "1px solid #f8b4b4",
    hoverBorder: "#dc3545",
  },
];

const PortfolioTotals = () => {
  const [hoveredIdx, setHoveredIdx] = React.useState(null);

  const getSparklineOptions = (color) => ({
    chart: {
      type: "area",
      sparkline: { enabled: true },
      animations: { enabled: true },
    },
    stroke: {
      curve: "smooth",
      width: 2,
      colors: [color],
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.05,
        stops: [0, 100],
        colorStops: [
          {
            offset: 0,
            color: color,
            opacity: 0.35,
          },
          {
            offset: 100,
            color: color,
            opacity: 0.0,
          },
        ],
      },
    },
    tooltip: {
      enabled: false,
    },
  });

  return (
    <Col xs={12} className="mt-3">
      <Card className="shadow-sm border">
        {/* Header */}
        <Card.Header className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between p-3 border-bottom gap-2">
          <div>
            <h6
              className="mb-0 fw-bold text-uppercase tracking-wide"
              style={{ fontSize: "14px", letterSpacing: "0.5px" }}
            >
              PORTFOLIO TOTALS &amp; EXECUTIVE PERFORMANCE SUMMARY
            </h6>
          </div>
        </Card.Header>

        {/* Card Body with 8 Grid Cards */}
        <Card.Body className="p-3">
          <Row className="g-3">
            {CARDS_DATA.map((item, idx) => {
              const IconComp = item.icon;
              const isHovered = hoveredIdx === idx;
              return (
                <Col xs={12} sm={6} md={6} lg={3} key={idx}>
                  <div
                    className="p-3 rounded h-100 d-flex flex-column justify-content-between"
                    onMouseEnter={() => setHoveredIdx(idx)}
                    onMouseLeave={() => setHoveredIdx(null)}
                    style={{
                      borderTop: isHovered
                        ? `1px solid ${item.hoverBorder || "#2c7ce6"}`
                        : item.cardBorder || "1px solid #e9ecef",
                      borderRight: isHovered
                        ? `1px solid ${item.hoverBorder || "#2c7ce6"}`
                        : item.cardBorder || "1px solid #e9ecef",
                      borderBottom: isHovered
                        ? `1px solid ${item.hoverBorder || "#2c7ce6"}`
                        : item.cardBorder || "1px solid #e9ecef",
                      borderLeft: item.leftBorder
                        ? item.leftBorder
                        : isHovered
                          ? `1px solid ${item.hoverBorder || "#2c7ce6"}`
                          : item.cardBorder || "1px solid #e9ecef",
                      boxShadow: isHovered
                        ? "0 8px 18px rgba(0, 0, 0, 0.09)"
                        : "0 2px 4px rgba(0, 0, 0, 0.03)",
                      transform: isHovered
                        ? "translateY(-3px)"
                        : "translateY(0)",
                      transition: "all 0.25s ease",
                      cursor: "pointer",
                    }}
                  >
                    {/* Top row: Title & Icon */}
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                      <span
                        className="fw-semi-bold text-uppercase"
                        style={{
                          fontSize: "11px",
                          color: item.titleColor || "#6c757d",
                          letterSpacing: "0.3px",
                        }}
                      >
                        {item.title}
                      </span>
                      <div
                        className="d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: "30px",
                          height: "30px",
                          borderRadius: "8px",
                          backgroundColor: item.iconBg,
                          color: item.iconColor,
                          border: `1px solid ${item.iconBorder}`,
                        }}
                      >
                        <IconComp size={15} />
                      </div>
                    </div>

                    {/* Middle row: Main Value, Badge & Sparkline */}
                    <div className="d-flex align-items-end justify-content-between gap-2 my-1">
                      <div>
                        <div
                          className="fw-bold font-monospace"
                          style={{
                            fontSize: "18px",
                            color: item.valueColor || "currentColor",
                          }}
                        >
                          {item.value}
                        </div>
                        {item.badgeText && (
                          <div className="mt-1">
                            <span
                              className="d-inline-block rounded px-2 py-0 fw-bold"
                              style={{
                                fontSize: "10px",
                                backgroundColor: item.badgeBg,
                                color: item.badgeColor,
                                lineHeight: "1.6",
                              }}
                            >
                              {item.badgeText}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Mini Sparkline Chart */}
                      <div
                        className="flex-shrink-0"
                        style={{ width: "75px", height: "32px" }}
                      >
                        <ApexCharts
                          options={getSparklineOptions(item.sparklineColor)}
                          series={[{ data: item.sparklineData }]}
                          type="area"
                          width={75}
                          height={32}
                        />
                      </div>
                    </div>

                    {/* Bottom row: Trend indicator & Note */}
                    <div className="d-flex align-items-center justify-content-between pt-2 border-top mt-2">
                      <div
                        className="d-flex align-items-center gap-1 fw-semi-bold"
                        style={{
                          fontSize: "10px",
                          color: item.trendUp ? "#00965e" : "#00965e",
                        }}
                      >
                        {item.trendUp ? (
                          <ArrowUpRight size={12} />
                        ) : (
                          <ArrowDownRight size={12} />
                        )}
                        <span>{item.trend}</span>
                      </div>
                      <span
                        className="text-muted text-truncate ms-1"
                        style={{ fontSize: "10px", maxWidth: "120px" }}
                      >
                        {item.note}
                      </span>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default PortfolioTotals;
